import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowRight } from 'lucide-react';

export default function WriterPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-4">Content Writer</h1>
        <p className="text-lg text-gray-700 mb-6">
          The AI-powered content writer is the core feature of AISEOBlogGenerator. This guide will help you understand
          how to use it effectively to create high-quality, SEO-optimized blog content.
        </p>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?q=80&w=2000&auto=format&fit=crop" 
          alt="Content Writer Interface" 
          className="w-full h-64 object-cover object-center"
        />
        <div className="p-6 bg-gray-50">
          <h3 className="font-semibold text-lg mb-2">Content Writer Interface</h3>
          <p className="text-gray-600">
            The content writer provides a user-friendly interface for creating blog posts with AI assistance.
          </p>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="features">Key Features</TabsTrigger>
          <TabsTrigger value="workflow">Workflow</TabsTrigger>
          <TabsTrigger value="tips">Tips & Tricks</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="pt-6">
          <div className="prose max-w-none">
            <h2>Content Writer Overview</h2>
            <p>
              The content writer is designed to help you create professional blog posts without the hassle of prompt engineering 
              or extensive editing. It uses advanced AI models to generate high-quality content based on your inputs.
            </p>
            
            <p>
              Unlike generic AI writing tools, our content writer is specifically designed for creating blog content that ranks well 
              in search engines while maintaining a natural, human-like tone.
            </p>

            <h3>Core Components</h3>
            <ul>
              <li><strong>Basic Options</strong> - Configure essential settings like title, keywords, and industry</li>
              <li><strong>Advanced Options</strong> - Fine-tune your content with readability levels, word count, and tone</li>
              <li><strong>Marketing Options</strong> - Optimize for conversions with audience targeting and CTAs</li>
              <li><strong>Writing Styles</strong> - Choose or create writing styles to match your brand voice</li>
            </ul>
          </div>
        </TabsContent>
        
        <TabsContent value="features" className="pt-6">
          <div className="prose max-w-none">
            <h2>Key Features</h2>
            
            <h3>Multiple AI Models</h3>
            <p>
              Choose from a variety of AI models depending on your subscription tier, including:
            </p>
            <ul>
              <li><strong>GPT-4o</strong> - Well-rounded model for general content</li>
              <li><strong>o1</strong> - Enhanced model with better reasoning capabilities</li>
              <li><strong>Claude 3.7 Sonnet</strong> - Superior model for longer, more nuanced content</li>
            </ul>
            
            <h3>Writing Styles</h3>
            <p>
              Select from pre-built writing styles inspired by popular authors and thought leaders, or create your own custom style.
            </p>
            
            <h3>Strike List</h3>
            <p>
              Maintain a list of words, phrases, and writing patterns you want the AI to avoid in your content.
            </p>
            
            <h3>Content Frameworks</h3>
            <p>
              Apply proven content frameworks like AIDA (Attention, Interest, Desire, Action) or PAS (Problem, Agitation, Solution) 
              to structure your blog posts effectively.
            </p>
          </div>
        </TabsContent>
        
        <TabsContent value="workflow" className="pt-6">
          <div className="prose max-w-none">
            <h2>Content Creation Workflow</h2>
            
            <ol>
              <li>
                <strong>Select AI model</strong>
                <p>Choose the most appropriate AI model for your content needs.</p>
              </li>
              <li>
                <strong>Enter title and keywords</strong>
                <p>Provide a compelling title and relevant keywords for SEO optimization.</p>
              </li>
              <li>
                <strong>Configure content options</strong>
                <p>Set industry, tone, readability level, and word count according to your requirements.</p>
              </li>
              <li>
                <strong>Enable or disable content features</strong>
                <p>Customize your content structure with options like introduction, conclusion, and key takeaways.</p>
              </li>
              <li>
                <strong>Add marketing options (optional)</strong>
                <p>Enhance conversion potential with targeted marketing parameters.</p>
              </li>
              <li>
                <strong>Generate content</strong>
                <p>Click the "Generate Blog Post" button and wait for the AI to create your content.</p>
              </li>
              <li>
                <strong>Review and export</strong>
                <p>Review the generated content and export it to your preferred platform.</p>
              </li>
            </ol>
          </div>
        </TabsContent>
        
        <TabsContent value="tips" className="pt-6">
          <div className="prose max-w-none">
            <h2>Tips & Best Practices</h2>
            
            <h3>For Better Results:</h3>
            <ul>
              <li>Be specific with your title and keywords to get more focused content</li>
              <li>Always enable "Longer Paragraphs" for more detailed, in-depth content</li>
              <li>Use "Key Takeaways" for better reader retention and SEO benefits</li>
              <li>Select the writing style that best matches your brand voice</li>
              <li>For technical content, set a higher readability level</li>
              <li>For general audiences, use grade 8-10 readability levels</li>
            </ul>
            
            <h3>Common Mistakes to Avoid:</h3>
            <ul>
              <li>Using too many keywords that don't naturally fit together</li>
              <li>Setting word count too low for complex topics</li>
              <li>Ignoring the Strike List feature when getting repetitive content</li>
              <li>Not providing enough context for highly specialized topics</li>
            </ul>
            
            <div className="bg-blue-50 p-4 rounded-md border border-blue-100 my-6">
              <h4 className="text-blue-800">Pro Tip</h4>
              <p className="mb-0">
                For the best results, try different AI models for the same content request. 
                Claude models often excel at nuanced topics, while GPT models can be better for technical content.
              </p>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="border rounded-lg p-6 mt-8 bg-gray-50">
        <h2 className="text-xl font-bold mb-4">Sample Content Writer Interface</h2>
        
        <div className="bg-white p-6 border rounded-md">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Blog Title</label>
            <input 
              type="text" 
              className="w-full px-3 py-2 border rounded-md" 
              placeholder="Enter your blog title"
              disabled
              value="10 Ways to Improve Your Content Marketing Strategy in 2025"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
            <input 
              type="text" 
              className="w-full px-3 py-2 border rounded-md" 
              placeholder="Enter keywords, separated by commas"
              disabled
              value="content marketing, marketing strategy, content optimization, SEO, lead generation"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
              <input 
                type="text" 
                className="w-full px-3 py-2 border rounded-md" 
                placeholder="e.g., Technology, Healthcare, Finance"
                disabled
                value="Digital Marketing"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tone</label>
              <select 
                className="w-full px-3 py-2 border rounded-md" 
                disabled
              >
                <option>Professional</option>
              </select>
            </div>
          </div>
          
          <div className="flex justify-center">
            <Button disabled className="w-full max-w-md bg-blue-800">
              Generate Blog Post
            </Button>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8 pt-6 border-t">
        <Button variant="outline" disabled>Previous: Getting Started</Button>
        <Button asChild>
          <Link to="/docs/writer/basic-options">
            Next: Basic Options
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}